/**
 * In-place updates of the `FormValidation` object shared between a form, its
 * nested field sets and the consumer (which also writes server-side errors into it).
 *
 * Each field set owns the keys of its own fields. Only keys whose errors actually
 * changed are written, so only the affected form groups re-render.
 */
/**
 * Writes the client-side validation results of one field set and resets the
 * form-level errors, like any edit does.
 */
export function applyFieldValidation(validation, keys, results) {
    if (validation.errors.length > 0) {
        validation.errors = [];
    }
    keys.forEach((key, index) => {
        const result = results[index];
        if (!sameErrors(validation.fields[key], result)) {
            validation.fields[key] = result;
        }
    });
}
/**
 * Removes the keys of a field set that is gone, e.g. a removed collection item.
 * Nested keys (`key.*`, `key[*]`) belong to nested field sets and are removed by those.
 */
export function clearFieldValidation(validation, keys) {
    keys.forEach(key => {
        if (key in validation.fields) {
            delete validation.fields[key];
        }
    });
}
/**
 * Drops the keys of a removed collection item (`prefix[index]...`) and moves the
 * keys of the following items up by one, so errors stay with their items.
 */
export function removeCollectionItemValidation(validation, prefix, index) {
    const pattern = new RegExp('^' + escapeRegExp(prefix) + '\\[(\\d+)\\](.*)$');
    const moved = {};
    Object.keys(validation.fields).forEach(key => {
        const match = pattern.exec(key);
        if (!match) {
            return;
        }
        const itemIndex = parseInt(match[1]);
        if (itemIndex < index) {
            return;
        }
        if (itemIndex > index) {
            moved[prefix + '[' + (itemIndex - 1) + ']' + match[2]] = validation.fields[key];
        }
        delete validation.fields[key];
    });
    Object.assign(validation.fields, moved);
}
function sameErrors(a, b) {
    if (a === b) {
        return true;
    }
    if (!a || a.length !== b.length) {
        return false;
    }
    return a.every((error, i) => error === b[i]);
}
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
